import { motion } from 'framer-motion';
import { BriefcaseBusiness, ClipboardList, DollarSign, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ProductInfoProps {
  product: any;
  className?: string;
}

export function ProductInfo({ product, className }: ProductInfoProps) {
  const { t } = useTranslation();
  if (!product) return null;

  return (
    <div className={className}>
      <h2 className="text-lg font-semibold mb-4 flex items-center">
        <Shield className="w-5 h-5 mr-2" />
        {product.name} - {product.type}
      </h2>

      <div className="space-y-4">
        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 p-4 rounded-lg"
        >
          <p className="text-sm text-gray-600">{product.description}</p>
        </motion.div>

        {/* Core Business */}
        {product.coreBusiness && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="space-y-2"
          >
            <h3 className="text-sm font-medium flex items-center">
              <BriefcaseBusiness className="w-4 h-4 mr-2" />
              {t('custom.roleplay.headers.coreBusiness')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1 ml-6">
              {product.coreBusiness.map((business: string, index: number) => (
                <li key={index} className="list-disc">
                  {business}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Key Features */}
        {product.keyFeatures && product.keyFeatures.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-2"
          >
            <h3 className="text-sm font-medium flex items-center">
              <ClipboardList className="w-4 h-4 mr-2" />
              {t('custom.roleplay.headers.keyFeatures')}
            </h3>
            <ul className="text-sm text-gray-600 space-y-1 ml-6">
              {product.keyFeatures.map((feature: string, index: number) => (
                <li key={index} className="list-disc">
                  {feature}
                </li>
              ))}
            </ul>
          </motion.div>
        )}

        {/* Pricing */}
        {product.pricing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-blue-50 p-4 rounded-lg"
          >
            <h3 className="text-sm font-medium flex items-center mb-2">
              <DollarSign className="w-4 h-4 mr-2" />
              {t('custom.roleplay.headers.pricing')}
            </h3>
            <p className="text-lg font-semibold text-blue-600 mb-2">
              {product.pricing.base}
            </p>
            <ul className="text-sm text-gray-600 space-y-1 ml-4">
              {product.pricing.details &&
                product.pricing.details.map((detail: string, index: number) => (
                  <li key={index} className="list-disc">
                    {detail}
                  </li>
                ))}
            </ul>
          </motion.div>
        )}
      </div>
    </div>
  );
}
